import type { ApplicationSettingsTabId } from '../../store/application-settings-tab-id';
import type { HomeScanningStatus } from './home-scanning-status';

export type TargetReadinessState = 'ready' | 'attention' | 'missing';

export type TargetReadinessKey =
  | 'credentials'
  | 'sourceCode'
  | 'scope'
  | 'network'
  | 'executions'
  | 'testingControls';

export type TargetReadinessInput = {
  credentials: { total: number; verified: number; failed: number };
  repositoryCount: number;
  isSourceCodeConnected: boolean;
  domainCount: number;
  signalCategoryCount: number;
  isInternalApp: boolean;
  hasInternalAccessNetwork: boolean;
  scanning: HomeScanningStatus;
  instructionCount: number;
  customGuardrailCount: number;
};

export type TargetReadinessItem = {
  key: TargetReadinessKey;
  state: TargetReadinessState;
  messageKey: string;
  values: Record<string, number>;
  section: ApplicationSettingsTabId;
};

const credentialsItem = ({
  credentials,
}: TargetReadinessInput): TargetReadinessItem => {
  const base = {
    key: 'credentials',
    section: 'credentials',
    values: credentials,
  } as const;

  if (credentials.total === 0) {
    return { ...base, state: 'missing', messageKey: 'none' };
  }

  if (credentials.failed > 0) {
    return { ...base, state: 'attention', messageKey: 'failed' };
  }

  if (credentials.verified === 0) {
    return { ...base, state: 'attention', messageKey: 'unverified' };
  }

  return { ...base, state: 'ready', messageKey: 'verified' };
};

const sourceCodeItem = ({
  isSourceCodeConnected,
  repositoryCount,
}: TargetReadinessInput): TargetReadinessItem => ({
  key: 'sourceCode',
  section: 'source-code',
  values: { repositoryCount },
  ...(isSourceCodeConnected
    ? { state: 'ready', messageKey: 'connected' }
    : { state: 'missing', messageKey: 'notConnected' }),
});

const scopeItem = ({
  domainCount,
  signalCategoryCount,
}: TargetReadinessInput): TargetReadinessItem => {
  const base = {
    key: 'scope',
    values: { domainCount, signalCategoryCount },
  } as const;

  if (domainCount === 0) {
    return {
      ...base,
      section: 'domains',
      state: 'missing',
      messageKey: 'noDomains',
    };
  }

  if (signalCategoryCount === 0) {
    return {
      ...base,
      section: 'signal-categories',
      state: 'missing',
      messageKey: 'noCategories',
    };
  }

  return {
    ...base,
    section: 'domains',
    state: 'ready',
    messageKey: 'defined',
  };
};

const networkItem = ({
  isInternalApp,
  hasInternalAccessNetwork,
}: TargetReadinessInput): TargetReadinessItem => {
  const base = { key: 'network', section: 'networking', values: {} } as const;

  if (!isInternalApp) {
    return { ...base, state: 'ready', messageKey: 'public' };
  }

  return hasInternalAccessNetwork
    ? { ...base, state: 'ready', messageKey: 'privateConnected' }
    : { ...base, state: 'missing', messageKey: 'privateMissing' };
};

const executionsItem = ({
  scanning,
}: TargetReadinessInput): TargetReadinessItem => {
  const base = {
    key: 'executions',
    section: 'automation',
    values: {},
  } as const;

  if (scanning.kind === 'adaptive') {
    return { ...base, state: 'ready', messageKey: 'adaptive' };
  }

  if (scanning.kind === 'paused') {
    return { ...base, state: 'attention', messageKey: 'paused' };
  }

  return scanning.enabled
    ? { ...base, state: 'ready', messageKey: 'on' }
    : { ...base, state: 'attention', messageKey: 'off' };
};

const testingControlsItem = ({
  instructionCount,
  customGuardrailCount,
}: TargetReadinessInput): TargetReadinessItem => ({
  key: 'testingControls',
  section:
    customGuardrailCount > 0 && instructionCount === 0
      ? 'guardrails'
      : 'instructions',
  values: { instructionCount, customGuardrailCount },
  ...(instructionCount + customGuardrailCount > 0
    ? { state: 'ready', messageKey: 'defined' }
    : { state: 'attention', messageKey: 'none' }),
});

const READINESS_STATE_ORDER: Record<TargetReadinessState, number> = {
  missing: 0,
  attention: 1,
  ready: 2,
};

export const buildTargetReadinessItems = (
  input: TargetReadinessInput,
): TargetReadinessItem[] =>
  [
    credentialsItem,
    scopeItem,
    networkItem,
    sourceCodeItem,
    executionsItem,
    testingControlsItem,
  ]
    .map((build) => build(input))
    .sort(
      (left, right) =>
        READINESS_STATE_ORDER[left.state] - READINESS_STATE_ORDER[right.state],
    );

export const countReadyItems = (items: TargetReadinessItem[]): number =>
  items.filter((item) => item.state === 'ready').length;
